import {
  Filter,
  Search,
  Wrench,
  ShieldCheck,
  AlertTriangle,
  MoreHorizontal,
  HelpCircle,
  Calendar,
  Clock,
  Image as ImageIcon,
  FileText
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import LoadingSpinner from '@/components/LoadingSpinner';

// Tipos
interface UserMachine {
  id: number;
  machineId: number;
  machineName: string;
  purchaseDate: string;
  status: 'operational' | 'maintenance' | 'warning';
  lastMaintenance: string;
  nextMaintenance: string;
  health: number;
  model?: string;
  serialNumber?: string;
  location?: string;
  images?: string[];
  productId?: number;
  imageUrl?: string;
}

interface UserMachinesProps {
  userData: any;
}

// Helper function to check if an image URL is valid
const isValidImageUrl = (url: string | undefined): boolean => {
  if (!url) return false;

  // Verificar que la URL no esté vacía y no contenga 'undefined' o 'null'
  if (url.trim() === '' || url.includes('undefined') || url.includes('null')) {
    return false;
  }

  // Si la URL es relativa, asumimos que es válida
  if (url.startsWith('/')) {
    return true;
  }

  // Verificar que la URL tenga un formato válido
  try {
    new URL(url);
    return true;
  } catch (e) {
    // Si no es una URL válida, podría ser una ruta relativa
    return url.startsWith('/') || url.startsWith('./') || url.startsWith('../');
  }
};

// Función para intentar diferentes formatos de URL de imagen
const getAlternativeImageUrl = (img: string): string | null => {
  // Si ya es una URL completa que no es de Supabase, devolverla tal cual
  if (img.startsWith('http') && !img.includes('supabase.co')) {
    return img;
  }

  // Si es una URL de Supabase, extraer la ruta del archivo
  if (img.includes('supabase.co') && img.includes('/storage/')) {
    const pathMatch = img.match(/\/images\/(.+)$/);
    if (pathMatch && pathMatch[1]) {
      // Construir URL directa
      const filePath = pathMatch[1];

      // Si la ruta ya incluye 'products/', usarla tal cual
      if (filePath.includes('products/')) {
        return `https://jlufgkgssnxumguvknhc.supabase.co/storage/v1/object/public/images/${filePath}`;
      }

      // Si no, asumir que es un nombre de archivo y añadir la ruta products/
      return `https://jlufgkgssnxumguvknhc.supabase.co/storage/v1/object/public/images/products/${filePath}`;
    }
  }

  // Si es una ruta relativa, construir URL completa
  if (!img.startsWith('http')) {
    // Limpiar la ruta
    const cleanPath = img.startsWith('/') ? img.substring(1) : img;

    // Si la ruta ya incluye 'products/', usarla tal cual
    if (cleanPath.includes('products/')) {
      return `https://jlufgkgssnxumguvknhc.supabase.co/storage/v1/object/public/images/${cleanPath}`;
    }

    // Si no, asumir que es un nombre de archivo y añadir la ruta products/
    return `https://jlufgkgssnxumguvknhc.supabase.co/storage/v1/object/public/images/products/${cleanPath}`;
  }

  // Extraer el nombre del archivo de cualquier URL
  if (img.includes('/')) {
    // Obtener el nombre del archivo de la ruta
    const parts = img.split('/');
    const fileName = parts[parts.length - 1];
    return `https://jlufgkgssnxumguvknhc.supabase.co/storage/v1/object/public/images/products/${fileName}`;
  }

  return null;
};

// Obtener máquinas del usuario desde Supabase
const fetchUserMachines = async (userId: string): Promise<UserMachine[]> => {
  if (!userId) {
    throw new Error("ID de usuario no disponible");
  }

  try {
    console.log("Obteniendo máquinas para el usuario:", userId);

    // Obtenemos las máquinas asignadas al usuario y hacemos join con la tabla products para obtener las imágenes
    const { data: userMachinesData, error: userMachinesError } = await supabase
      .from('user_machines')
      .select(`
        *,
        products:machine_id (
          id,
          name,
          images,
          brand,
          model:technical_specs->model
        )
      `)
      .eq('user_id', userId)
      .order('id', { ascending: false });

    if (userMachinesError) {
      console.error("Error al obtener las máquinas del usuario:", userMachinesError);
      throw userMachinesError;
    }

    if (!userMachinesData || userMachinesData.length === 0) {
      console.log("No se encontraron máquinas para el usuario", userId);
      return [];
    }

    console.log("Máquinas encontradas:", userMachinesData.length);

    // Preparamos las máquinas con datos de mantenimiento e imágenes
    const machines: UserMachine[] = userMachinesData.map((machine: any) => {
      // Calcular fechas de mantenimiento basadas en la fecha de compra
      const purchaseDate = new Date(machine.purchase_date);

      // Última fecha de mantenimiento (simulada: 3 meses después de la compra)
      const lastMaintenanceDate = new Date(purchaseDate);
      lastMaintenanceDate.setMonth(purchaseDate.getMonth() + 3);

      // Próxima fecha de mantenimiento (simulada: 6 meses después de la compra)
      const nextMaintenanceDate = new Date(purchaseDate);
      nextMaintenanceDate.setMonth(purchaseDate.getMonth() + 6);

      // Calcular salud de la máquina de manera dinámica (entre 60-100%)
      const today = new Date();
      const daysSinceLastMaintenance = Math.max(0, Math.floor((today.getTime() - lastMaintenanceDate.getTime()) / (1000 * 60 * 60 * 24)));

      // La salud disminuye con el tiempo desde el último mantenimiento
      const health = Math.max(60, Math.min(100, 100 - Math.floor(daysSinceLastMaintenance / 30) * 5));

      // Estado de la máquina basado en su salud
      let status: 'operational' | 'maintenance' | 'warning' = 'operational';
      if (health < 70) {
        status = 'warning';
      } else if (nextMaintenanceDate.getTime() < today.getTime()) {
        status = 'maintenance';
      }

      // Obtener imágenes y detalles del producto si existen
      const productInfo = machine.products || {};
      const images = productInfo.images || [];
      const productModel = productInfo.model || 'Modelo estándar';
      const productId = productInfo.id;

      return {
        id: machine.id,
        machineId: machine.machine_id,
        machineName: machine.machine_name,
        purchaseDate: machine.purchase_date,
        status: status,
        lastMaintenance: lastMaintenanceDate.toISOString(),
        nextMaintenance: nextMaintenanceDate.toISOString(),
        health: health,
        model: productModel,
        serialNumber: machine.serial_number || `SN-${Math.floor(Math.random() * 10000)}`,
        location: machine.location || 'Planta principal',
        images: images,
        productId: productId,
        imageUrl: images && images.length > 0 ? images[0] : null
      };
    });

    return machines;
  } catch (error) {
    console.error("Error al obtener las máquinas del usuario:", error);
    return [];
  }
};

export default function UserMachines({ userData }: UserMachinesProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [brokenImages, setBrokenImages] = useState<Record<string, boolean>>({});
  const [loadingImages, setLoadingImages] = useState<Record<string, boolean>>({});

  // Usar React Query para obtener las máquinas
  const {
    data: machines = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['userMachines', userData?.id],
    queryFn: () => fetchUserMachines(userData?.id),
    enabled: !!userData?.id,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  // Formatear fecha
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  // Obtener el estado de cada máquina
  const getMachineStatusInfo = (status: string) => {
    switch (status) {
      case 'operational':
        return {
          label: 'Operativa',
          color: 'bg-emerald-500',
          textColor: 'text-emerald-500',
          icon: <ShieldCheck className="h-4 w-4" />
        };
      case 'maintenance':
        return {
          label: 'En mantenimiento',
          color: 'bg-blue-500',
          textColor: 'text-blue-500',
          icon: <Wrench className="h-4 w-4" />
        };
      case 'warning':
        return {
          label: 'Necesita revisión',
          color: 'bg-amber-500',
          textColor: 'text-amber-500',
          icon: <AlertTriangle className="h-4 w-4" />
        };
      default:
        return {
          label: 'Desconocido',
          color: 'bg-gray-500',
          textColor: 'text-gray-500',
          icon: <HelpCircle className="h-4 w-4" />
        };
    }
  };

  // Filtrar máquinas
  const filteredMachines = machines.filter(machine => {
    // Filtrar por búsqueda
    const matchesSearch =
      machine.machineName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      machine.model?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      machine.serialNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      machine.location?.toLowerCase().includes(searchQuery.toLowerCase());

    // Filtrar por estado
    const matchesStatus = statusFilter === 'all' || machine.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Contar máquinas por estado
  const getMachineCountByStatus = () => {
    return {
      operational: machines.filter(m => m.status === 'operational').length,
      maintenance: machines.filter(m => m.status === 'maintenance').length,
      warning: machines.filter(m => m.status === 'warning').length,
      total: machines.length
    };
  };

  const machineStats = getMachineCountByStatus();

  // Función para manejar errores de carga de imágenes
  const handleImageError = (imageUrl: string, e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    try {
      // Marcar la imagen como rota
      setBrokenImages(prev => ({ ...prev, [imageUrl]: true }));

      // Intentar con una URL alternativa
      const alternativeUrl = getAlternativeImageUrl(imageUrl);

      if (alternativeUrl && !brokenImages[alternativeUrl]) {
        // Establecer la nueva URL
        const imgElement = e.currentTarget;
        if (imgElement) {
          imgElement.src = alternativeUrl;

          // Añadir un segundo manejador de error como respaldo
          imgElement.onerror = () => {
            // Si la segunda URL también falla, usar el placeholder
            setBrokenImages(prev => ({ ...prev, [alternativeUrl]: true }));
            if (imgElement) {
              imgElement.src = "/placeholder-image.png";
              imgElement.onerror = null; // Evitar bucles infinitos
            }
          };
        }
      } else {
        // Si no hay alternativa o ya falló, usar placeholder
        const imgElement = e.currentTarget;
        if (imgElement) {
          imgElement.src = "/placeholder-image.png";
          imgElement.onerror = null;
        }
      }
    } catch (error) {
      // En caso de error, usar el placeholder
      const imgElement = e.currentTarget;
      if (imgElement) {
        imgElement.src = "/placeholder-image.png";
      }
      // Marcar la imagen como rota
      setBrokenImages(prev => ({ ...prev, [imageUrl]: true }));
    }
  };

  // Función para obtener la imagen de la máquina
  const getMachineImage = (machine: UserMachine) => {
    // Si tiene un array de imágenes
    if (machine.images && Array.isArray(machine.images) && machine.images.length > 0) {
      const firstImage = machine.images[0];

      // Verificar si la imagen es válida y no está marcada como rota
      if (isValidImageUrl(firstImage) && !brokenImages[firstImage]) {
        return firstImage;
      }
    }

    // Si tiene una URL de imagen
    if (machine.imageUrl && isValidImageUrl(machine.imageUrl) && !brokenImages[machine.imageUrl]) {
      return machine.imageUrl;
    }

    // Si no hay imagen válida, intentar generar alternativas
    if (machine.images && machine.images.length > 0) {
      const alternativeUrl = getAlternativeImageUrl(machine.images[0]);
      if (alternativeUrl && !brokenImages[alternativeUrl]) {
        return alternativeUrl;
      }
    }

    // Devolver imagen por defecto si todo falla
    return '/placeholder-image.png';
  };

  // Render pantalla de carga
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  // Render estado de error
  if (error) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">
            Mis Máquinas
          </h1>
          <p className="text-zinc-400">
            Ha ocurrido un error al cargar tus máquinas.
          </p>
        </div>

        <div className="bg-red-900/20 border border-red-800/50 rounded-xl p-8 text-center">
          <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-2" />
          <h2 className="text-xl font-semibold text-white mb-2">Error al cargar datos</h2>
          <p className="text-zinc-400 max-w-md mx-auto mb-6">
            No pudimos cargar la información de tus máquinas. Por favor, intenta recargar la página o contacta con soporte técnico.
          </p>
          <Button
            variant="outline"
            className="border-zinc-700 hover:bg-zinc-700 text-white"
            onClick={() => window.location.reload()}
          >
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  // Render estado sin máquinas
  if (machines.length === 0) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">
            Mis Máquinas
          </h1>
          <p className="text-zinc-400">
            Aún no tienes máquinas registradas en tu cuenta.
          </p>
        </div>

        <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-xl p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-zinc-700/50 rounded-full flex items-center justify-center mb-4">
            <HelpCircle className="h-8 w-8 text-zinc-500" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">No hay máquinas disponibles</h2>
          <p className="text-zinc-400 max-w-md mx-auto mb-6">
            Todavía no tienes máquinas asignadas a tu cuenta. Contacta con tu administrador o representante de ventas para que asigne tus máquinas.
          </p>
          <Button
            variant="outline"
            className="border-zinc-700 hover:bg-zinc-700 text-white"
          >
            Contactar con soporte
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Encabezado */}
      <div>
        <h1 className="text-2xl font-bold text-white mb-2">
          Mis Máquinas
        </h1>
        <p className="text-zinc-400">
          Gestiona todas tus máquinas y consulta su estado detallado.
        </p>
      </div>

      {/* Estadísticas resumen */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-800/50 border-zinc-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-400">Total máquinas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline space-x-2">
              <span className="text-3xl font-bold text-white">{machineStats.total}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-800/50 border-zinc-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-400">Operativas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline space-x-2">
              <span className="text-3xl font-bold text-emerald-500">{machineStats.operational}</span>
              <span className="text-sm text-zinc-500">
                {machineStats.total > 0 ? `(${Math.round((machineStats.operational / machineStats.total) * 100)}%)` : ''}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-800/50 border-zinc-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-400">En mantenimiento</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline space-x-2">
              <span className="text-3xl font-bold text-blue-500">{machineStats.maintenance}</span>
              <span className="text-sm text-zinc-500">
                {machineStats.total > 0 ? `(${Math.round((machineStats.maintenance / machineStats.total) * 100)}%)` : ''}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-800/50 border-zinc-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-400">Necesitan revisión</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline space-x-2">
              <span className="text-3xl font-bold text-amber-500">{machineStats.warning}</span>
              <span className="text-sm text-zinc-500">
                {machineStats.total > 0 ? `(${Math.round((machineStats.warning / machineStats.total) * 100)}%)` : ''}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros y búsqueda */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-500" />
          <Input
            type="search"
            placeholder="Buscar por nombre, modelo, número de serie..."
            className="pl-8 bg-zinc-800/50 border-zinc-700 text-white"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={setStatusFilter}
        >
          <SelectTrigger className="w-full sm:w-[180px] bg-zinc-800/50 border-zinc-700 text-white">
            <SelectValue placeholder="Filtrar por estado" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700 text-white">
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="operational">Operativas</SelectItem>
            <SelectItem value="maintenance">En mantenimiento</SelectItem>
            <SelectItem value="warning">Necesitan revisión</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Lista de máquinas */}
      <div className="space-y-4">
        {filteredMachines.length === 0 ? (
          <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-lg p-8 text-center">
            <p className="text-zinc-400">No se encontraron máquinas con los filtros aplicados</p>
          </div>
        ) : (
          filteredMachines.map((machine) => {
            const status = getMachineStatusInfo(machine.status);
            const machineImage = getMachineImage(machine);

            return (
              <Card
                key={machine.id}
                className="bg-zinc-800/50 border-zinc-700/50 hover:border-[#FCAA0D]/30 transition-colors"
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Imagen de la máquina */}
                    <div className="md:w-[180px] h-[180px] flex-shrink-0">
                      {loadingImages[machineImage] && (
                        <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/80 z-10">
                          <LoadingSpinner />
                        </div>
                      )}
                      {machineImage && machineImage !== '/placeholder-image.png' ? (
                        <div className="w-full h-full rounded-lg overflow-hidden bg-zinc-900/50 border border-zinc-700/50 relative">
                          <img
                            src={machineImage}
                            alt={`${machine.machineName}`}
                            className="w-full h-full object-cover"
                            onError={(e) => handleImageError(machineImage, e)}
                            onLoad={() => setLoadingImages(prev => ({ ...prev, [machineImage]: false }))}
                            onLoadStart={() => setLoadingImages(prev => ({ ...prev, [machineImage]: true }))}
                          />
                        </div>
                      ) : (
                        <div className="w-full h-full rounded-lg bg-zinc-900/50 border border-zinc-700/50 flex items-center justify-center">
                          <FileText className="h-12 w-12 text-zinc-700" />
                        </div>
                      )}
                    </div>

                    {/* Información de la máquina */}
                    <div className="flex-1">
                      <div className="flex flex-col space-y-4">
                        {/* Encabezado de la máquina */}
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-lg font-semibold text-white">{machine.machineName}</h3>
                              <Badge
                                variant="outline"
                                className={`${status.textColor} border-${status.color}/30 bg-${status.color}/10`}
                              >
                                <span className="flex items-center gap-1">
                                  {status.icon}
                                  {status.label}
                                </span>
                              </Badge>
                            </div>
                            <p className="text-sm text-zinc-400">ID: {machine.machineId} | Modelo: {machine.model}</p>
                          </div>
                          <div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-white">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-zinc-800 border-zinc-700 text-white">
                                <DropdownMenuItem className="hover:bg-zinc-700 cursor-pointer">
                                  Ver detalles
                                </DropdownMenuItem>
                                <DropdownMenuItem className="hover:bg-zinc-700 cursor-pointer">
                                  Historial de mantenimiento
                                </DropdownMenuItem>
                                <DropdownMenuItem className="hover:bg-zinc-700 cursor-pointer">
                                  Programar mantenimiento
                                </DropdownMenuItem>
                                <DropdownMenuItem className="hover:bg-zinc-700 cursor-pointer">
                                  Reportar problema
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>

                        {/* Detalles de la máquina */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {/* Columna 1: Información */}
                          <div className="space-y-2">
                            <div className="flex gap-2 text-sm items-center">
                              <Calendar className="h-4 w-4 text-zinc-500" />
                              <span className="text-zinc-400">Compra: {formatDate(machine.purchaseDate)}</span>
                            </div>

                            <div className="flex gap-2 text-sm items-center">
                              <Clock className="h-4 w-4 text-zinc-500" />
                              <span className="text-zinc-400">Último mantenimiento: {formatDate(machine.lastMaintenance)}</span>
                            </div>

                            <div className="flex gap-2 text-sm items-center">
                              <Wrench className="h-4 w-4 text-zinc-500" />
                              <span className="text-zinc-400">Próximo mantenimiento: {formatDate(machine.nextMaintenance)}</span>
                            </div>
                          </div>

                          {/* Columna 2: Información adicional */}
                          <div className="space-y-2">
                            <div className="text-sm">
                              <span className="text-zinc-500">Número de serie:</span>
                              <span className="text-zinc-300 ml-2">{machine.serialNumber}</span>
                            </div>
                            <div className="text-sm">
                              <span className="text-zinc-500">Ubicación:</span>
                              <span className="text-zinc-300 ml-2">{machine.location}</span>
                            </div>
                            <div className="text-sm">
                              <span className="text-zinc-500">Última incidencia:</span>
                              <span className="text-zinc-300 ml-2">Ninguna registrada</span>
                            </div>
                          </div>

                          {/* Columna 3: Estado de salud */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-zinc-400">Estado de salud</span>
                              <span className={machine.health > 80 ? 'text-emerald-400' : machine.health > 50 ? 'text-amber-400' : 'text-red-400'}>
                                {machine.health}%
                              </span>
                            </div>
                            <Progress
                              value={machine.health}
                              className="h-2 bg-zinc-700"
                              style={{
                                '--progress-foreground': machine.health > 80
                                  ? 'hsl(142, 76%, 36%)' // emerald-500
                                  : machine.health > 50
                                    ? 'hsl(43, 96%, 58%)' // amber-500
                                    : 'hsl(0, 84%, 60%)' // red-500
                              } as React.CSSProperties}
                            />

                            <div className="flex justify-end mt-3">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-zinc-300 border-zinc-700 hover:bg-zinc-700 hover:text-white">
                                Ver detalles
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
